import { useFlagsmithLoading } from '@flagsmith/flagsmith/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { SUCCESS_URL } from '@/App';
import copyIcon from '@/assets/images/copy.svg';
import { CountDown } from '@/components/CountDown';
import { CurvedTextWithStars } from '@/components/CurvedTextWithStars/CurvedTextWithStars';
import { MarketingBanners } from '@/components/MarketingBanners/MarketingBanners';
import StreakBanner from '@/components/Streak/StreakBanner';
import { SuccessSummary } from '@/components/SuccessSummary/SuccessSummary';
import { SuccessTitle } from '@/components/SuccessTitle/SuccessTitle';
import { BUTTON_SOURCES } from '@/constants/buttonSources';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useFeatureFlag, useFeatureFlagValue } from '@/hooks/useFeatureFlag';
import { useGetCashbackPercentageValue } from '@/hooks/useGetCashbackPercentageValue';
import { useGetPaymentSummary } from '@/hooks/useGetPaymentSummary';
import {
  clearPaymentTimestamps,
  getFailedPaymentsCount,
  getPaymentProcessLatency,
  getPaymentTimestamps,
  getSystemInfo,
  resetFailedPaymentsCount,
  sendBusinessEvent,
  setPaymentEndTimestamp,
} from '@/screens/Checkout/monitoring';
import { useBranding } from '@/utils/branding';
import { trackButtonClick } from '@/utils/gtmButtonClicked';
import styles from './PaymentConfirmation.module.scss';

export const PaymentConfirmation = () => {
  const { t } = useTranslation();
  const screenContainerRef = useRef<HTMLDivElement>(null);
  const eventSentRef = useRef<boolean>(false);
  const [copyFeedback, setCopyFeedback] = useState<string>('');
  const [isDownloadFallbackEnabled, setIsDownloadFallbackEnabled] = useState(false);
  const query = new URLSearchParams(useLocation().search);
  const browserUserId = useBrowserUserId();
  const {
    orderValue = '',
    order = '',
    orderNumber,
    brand,
    brandId,
    outletId,
    serviceFeeAmount,
    isServiceFeeEnabled,
    tableNumber,
    counterNumber,
    splitBillEnabled,
    numberOfSplits,
    payingFor,
    splitType,
    paymentMethod = 'unknown',
    cashback,
    bonus,
  } = Object.fromEntries(query.entries());

  const { data: paymentSummary } = useGetPaymentSummary({ orderNumber });
  const deferredLinkFeatureValue = useFeatureFlagValue('deferred-link');
  const featureIsLoading = useFlagsmithLoading();
  const cashbackPercentageValue = useGetCashbackPercentageValue();

  const homeLink = tableNumber
    ? `/?outletId=${outletId}&tableNumber=${tableNumber}&brandId=${brandId}`
    : counterNumber
      ? `/?outletId=${outletId}&counterNumber=${counterNumber}&brandId=${brandId}`
      : '/';

  const deferredLink = useMemo(() => {
    if (featureIsLoading?.isLoading || featureIsLoading?.isFetching || !paymentSummary) {
      return '';
    }
    const base = `${deferredLinkFeatureValue}${order}`;
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}orderAmount=${paymentSummary.orderAmount}&s=${bonus === '1' ? 'true' : 'false'}`;
  }, [
    bonus,
    featureIsLoading,
    deferredLinkFeatureValue,
    order,
    paymentSummary,
    paymentSummary?.orderAmount,
  ]);

  const sharedUrlParams = {
    brandId: brandId || undefined,
    orderId: order || undefined,
    tableNumber,
    counterNumber,
  };

  const handleDownloadClick = () => {
    setIsDownloadFallbackEnabled(true);
    trackButtonClick({
      buttonId: 'download-app-success',
      buttonName: t('paymentConfirmation.paymentConfirmation.downloadApp'),
      buttonSource: BUTTON_SOURCES.SUMMARY_SCREEN,
      urlParams: sharedUrlParams,
      extraParams: {
        browser_user_id: browserUserId,
      },
    });
  };

  const handleHomeClick = () => {
    trackButtonClick({
      buttonId: 'close-success-screen-home-link',
      buttonName: 'Home link',
      buttonSource: BUTTON_SOURCES.SUMMARY_SCREEN,
      urlParams: sharedUrlParams,
      extraParams: {
        browser_user_id: browserUserId,
      },
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(deferredLink);
      setCopyFeedback(t('paymentConfirmation.paymentConfirmation.copied'));
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      setCopyFeedback(t('paymentConfirmation.paymentConfirmation.failedToCopy'));
      setTimeout(() => setCopyFeedback(''), 2000);
    }
  };

  useEffect(() => {
    // Set payment end timestamp when user sees the success page
    setPaymentEndTimestamp();

    const element = screenContainerRef.current;
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Send screen viewed business event
  useEffect(() => {
    if (order && paymentSummary) {
      sendBusinessEvent('kitopay.screen.viewed', {
        screen: 'payment_success',
        orderId: order,
        cashbackAmount: cashback || '0',
        hasCashback: cashback !== '0',
      });
    }
  }, [order, paymentSummary, cashback]);

  useEffect(() => {
    // Send purchase_completed event only once
    if (!eventSentRef.current && order && brand) {
      const sendEvent = () => {
        const payload = {
          orderId: order,
          brandName: brand,
          success_status: SUCCESS_URL, // Legacy field
          sourceScreen: 'payment success', // New field of screen where event occured
          order_value: orderValue,
          ab_group: isServiceFeeEnabled ? 1 : 0,
          service_fee_amount: serviceFeeAmount,
          split_bill_enabled: splitBillEnabled,
          number_of_splits: numberOfSplits,
          paying_for: payingFor,
          ...(splitType ? { split_type: splitType } : {}),
          paymentMethod,
          numberOfFailedPaymentsInARow: getFailedPaymentsCount(),
          ...getPaymentTimestamps(),
          paymentProcessLatency: getPaymentProcessLatency(),
          ...getSystemInfo(),
          browser_user_id: browserUserId,
        };

        if (typeof window.gtag === 'function') {
          window.gtag('event', 'purchase_completed', { ...payload });
          eventSentRef.current = true;

          // Reset failed payments counter and clear timestamps after sending the event
          resetFailedPaymentsCount();
          clearPaymentTimestamps();
        }
      };

      sendEvent();
    }
  }, [
    order,
    brand,
    orderValue,
    isServiceFeeEnabled,
    serviceFeeAmount,
    splitBillEnabled,
    numberOfSplits,
    payingFor,
    splitType,
    paymentMethod,
    browserUserId,
  ]);

  const isStreakBannerEnabled = useFeatureFlag('streak-banner');
  const { showAcquisition, showCashbackUI, showAppLinks } = useBranding();

  const hasCashback = cashback !== '0';

  return (
    <div className={styles.root} ref={screenContainerRef}>
      <div className={styles.success}>
        <div
          className={
            showCashbackUI && cashbackPercentageValue && hasCashback ? styles.beams : undefined
          }
        >
          <SuccessTitle />

          <Link className={styles.homeLink} to={homeLink} onClick={handleHomeClick} />
          {showAcquisition && isStreakBannerEnabled && !hasCashback && <StreakBanner />}
          <div className={styles.tile}>
            {showAcquisition && hasCashback && <CountDown startTime={[59, 59]} />}

            {showAppLinks && deferredLink !== '' && (
              <a
                className={styles.button}
                href={deferredLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDownloadClick}
              >
                {!hasCashback
                  ? t('paymentConfirmation.paymentConfirmation.downloadKitopiApp')
                  : t('paymentConfirmation.paymentConfirmation.downloadApp')}
              </a>
            )}

            {showAppLinks && isDownloadFallbackEnabled && (
              <div className={styles.claimFallback}>
                <div className={styles.copyText}>
                  {t('paymentConfirmation.paymentConfirmation.troubleDownloading')}
                </div>
                <button
                  className={styles.copyButton}
                  onClick={handleCopyLink}
                  title={t('paymentConfirmation.paymentConfirmation.copyLink')}
                >
                  <img
                    src={copyIcon}
                    alt={t('paymentConfirmation.paymentConfirmation.copy')}
                    className={styles.copyIcon}
                  />
                </button>
                {copyFeedback && <span className={styles.copyFeedback}>{copyFeedback}</span>}
              </div>
            )}

            <div className={styles.summaryWrapper}>
              <MarketingBanners />
              <SuccessSummary />
            </div>

            <div className={styles.disclaimer}>
              {t('paymentConfirmation.paymentConfirmation.askForReceipt')}
            </div>

            {showAcquisition && !cashbackPercentageValue && !featureIsLoading && (
              <CurvedTextWithStars
                className={styles.hopeYouEnjoyed}
                shadowColor="transparent"
                starsColor="#c49eed"
                verticalSpacing={65}
                starsPadding={25}
                textLines={[
                  {
                    text: t('paymentConfirmation.paymentConfirmation.hopeYouEnjoyed.firstLine'),
                    fontSize: 65,
                  },
                  {
                    text: t('paymentConfirmation.paymentConfirmation.hopeYouEnjoyed.secondLine'),
                    fontSize: 65,
                  },
                  {
                    text: t('paymentConfirmation.paymentConfirmation.hopeYouEnjoyed.thirdLine'),
                    fontSize: 65,
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
